import * as React from 'react';
import * as R from 'ramda';
import { Form, Text, FormValues, FormErrors, Select, Checkbox } from 'react-form';
import { BaseFormProps, BaseFormState } from '../../interfaces';
import { ValidationService, TenantDataService, SurveyDataService } from '../../services';
import { Tenant, SurveyReportType, SurveyText } from '../../models';
import { observer, inject } from 'mobx-react';
import { PonderaEditModal } from '../../components';
import { ServiceStore } from '../../stores';

interface FormProps extends BaseFormProps {
    ServiceStore?: ServiceStore;
}

interface FormState extends BaseFormState {
    tenants: Tenant[];
    email: string;
}

@inject('ServiceStore') @observer
export class SurveyForm extends React.Component<FormProps, FormState> {
    private vs = new ValidationService();
    private tenantDataService: TenantDataService;
    private surveyDataService: SurveyDataService;

    constructor(props: BaseFormProps) {
        super(props);

        this.state = {
            canSubmit: false,
            formErrors: {},
            tenants: [],
            email: ''
        };

        this.props.defaultValues.surveyReportTypes = this.props.defaultValues.surveyReportTypes || [];
        this.props.defaultValues.surveyTexts = this.props.defaultValues.surveyTexts || [];

        this.tenantDataService = new TenantDataService(this.props.ServiceStore!.notificationService);
        this.surveyDataService = new SurveyDataService(this.props.ServiceStore!.notificationService);

        this.validateErrors = this.validateErrors.bind(this);
        this.validateSuccess = this.validateSuccess.bind(this);
    }

    componentDidMount() {
        this.tenantDataService.getAll().then((tenants: Tenant[]) => {
            this.setState({ tenants: tenants });
        });
    }

    // Validation methods
    validateErrors(values: FormValues) {
        const { name, tenantID } = values;
        let formErrors: any = {};

        if (name || name === '') {
            if (!this.vs.required(name)) {
                formErrors.name = 'Name is required.';
            } else if (!this.vs.cannotBeEmpty(name!)) {
                formErrors.name = 'Name cannot be blank.';
            }
        }

        if (tenantID) {
            if (!this.vs.required(tenantID)) {
                formErrors.tenantId = 'Tenant is required.';
            }
        }

        return formErrors;
    }

    validateSuccess(values: FormValues, errors: FormErrors) {
        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && this.vs.validateRequiredFieldsExist(['name', 'tenantID'], values)) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }
    // end of validation stuff

    private submit(values: any) {
        if (values.surveyTexts) {
            values.surveyTexts.map((d: any) => {
                delete d.showModal;
            });
        }
        this.props.submit(values);
    }

    private submitForm(formApi: any, e: any) {
        formApi.submitForm(e);
    }

    public render() {
        const { canSubmit, formErrors, tenants } = this.state;
        const tenantOptions = !tenants ? [] : tenants.map((tenant) => {
            return {
                label: tenant.name,
                value: tenant.id
            };
        });

        const demographics = [
            { name: 'Birth Date', collectProperty: 'collectBirthDate', requiredProperty: 'requireBirthDate' },
            { name: 'Birth Place', collectProperty: 'collectBirthPlace', requiredProperty: 'requireBirthPlace' },
            { name: 'Education Level', collectProperty: 'collectEducationLevel', requiredProperty: 'requireEducationLevel' },
            { name: 'Gender', collectProperty: 'collectGender', requiredProperty: 'requireGender' },
            { name: 'Title', collectProperty: 'collectTitle', requiredProperty: 'requireTitle' }
        ];

        const languageOpts: any = [{
            label: 'English',
            value: 1
        }];

        return (
            <Form
                defaultValues={this.props.defaultValues}
                onSubmit={submittedValues => this.submit(submittedValues)}
                validateError={this.validateErrors}
                validateSuccess={this.validateSuccess}
            >
                {formApi => (
                    <form onSubmit={(e) => this.submitForm(formApi, e)} id="entityForm">
                        <div className="form-group">
                            <label htmlFor="name">Name *</label>
                            <Text className="form-control" field="name" id="name" />
                            {formErrors.name && <span className="help-block error-text">{formErrors.name}</span>}
                        </div>
                        <div className="form-group">
                            <label htmlFor="tenantID">Tenant *</label>
                            <Select className="form-control" field="tenantID" id="tenantID" options={tenantOptions} />
                            {formErrors.name && <span className="help-block error-text">{formErrors.tenantID}</span>}
                        </div>

                        <h3>Demographics</h3>
                        <i>These are optional pieces of information that can be gathered from the individual at the start of an assessment</i>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Collect?</th>
                                    <th>Required?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {demographics.map((item) => {
                                    return (
                                        <tr key={`demogrpahic-${item.name}`}>
                                            <td>{item.name}</td>
                                            <td>
                                                <Checkbox className="checkbox" field={item.collectProperty} />
                                            </td>
                                            <td>
                                                <Checkbox className="checkbox" field={item.requiredProperty} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <h3>Report Types</h3>
                        <i>These are the different available types of the Individual Report. The sort (first report) decides which report type will be selected by default if an organization hasn't been specifically assigned a report type.</i>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {formApi.values.surveyReportTypes.sort((one: SurveyReportType, two: SurveyReportType) => {
                                    if (one.sort > two.sort) {
                                        return 1;
                                    } else if (one.sort < two.sort) {
                                        return -1;
                                    }
                                    return 0;
                                }).map((item: SurveyReportType, ind: number) => {
                                    return (
                                        <tr key={`report-type-${ind}`}>
                                            <td>
                                                <Text className="form-control" field={['surveyReportTypes', ind, 'name']}  id={`name-${item.id}-${ind}`} />
                                            </td>
                                            <td>
                                                <Text className="form-control" field={['surveyReportTypes', ind, 'sort']}  id={`sort-${item.id}-${ind}`} />
                                            </td>
                                            <td>
                                                <a className="btn btn-danger" onClick={(e) => { formApi.values.surveyReportTypes.splice(ind, 1); this.forceUpdate(); }}>Remove</a>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <a className="btn btn-default" onClick={(e) => { formApi.values.surveyReportTypes.push(new SurveyReportType()); this.forceUpdate(); }}>Add Report Type</a>

                        <h3>About Individual Report Content</h3>
                        {this.textBlock(formApi, languageOpts, 'individualReport')}
                        <hr />
                        <h3>Email</h3>
                        {this.textBlock(formApi, languageOpts, 'email')}
                        <div className="row">
                            <div className="col-xs-8">
                                <input className="form-control" type="text" value={this.state.email} onChange={(e) => this.setState({email: e.target.value})} />
                            </div>
                            <div className="col-xs-4">
                                <a className="btn btn-warning btn-block" onClick={(e) => this.sendTestEmail()}>Send Test Email</a>
                            </div>
                        </div>
                        <hr />

                        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>Save</button>
                    </form>
                )}
            </Form>
        );
    }

    private sendTestEmail() {
        if (this.props.defaultValues) {
            this.surveyDataService.testEmail(this.props.defaultValues.id, this.state.email);
        }
    }

    private textBlock(formApi: any, languageOpts: any[], type: string) {
        return (
            <div>
                <table className="table">
                    <tr>
                        <td>
                            Language
                        </td>
                        <td>
                            Text
                        </td>
                    </tr>
                    {
                        formApi.values.surveyTexts.map((text: SurveyText, index: number) => {
                            if (text.surveyTextType !== type) {
                                return;
                            }

                            var availableKeys = <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <td>Key</td>
                                        <td>Description</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>#{'{'}FIRSTNAME{'}'}</td>
                                        <td>The first name of the user.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}LASTNAME{'}'}</td>
                                        <td>The last name of the user.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}ORGANIZATION{'}'}</td>
                                        <td>The name of the organization the user is in.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}TOTALSTATEMENTS{'}'}</td>
                                        <td>The total number of statements in the survey.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}TRUEFALSECOUNT{'}'}</td>
                                        <td>The total number of true/false statements in the survey.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}LIKERTCOUNT{'}'}</td>
                                        <td>The total number of likert statements in the survey.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}SURVEYEXPIRATION{'}'}</td>
                                        <td>The date the survey will expire.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}ASSESSMENTLINKSHORT{'}'}</td>
                                        <td>Clickable short link to the survey, labeled 'Take the Assessment'.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}ASSESSMENTLINK{'}'}</td>
                                        <td>Clickable long link to the survey, is the full http:// address.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}SENDERFIRSTNAME{'}'}</td>
                                        <td>The first name of the person sending the survey invite.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}SENDERLASTNAME{'}'}</td>
                                        <td>The last name of the person sending the survey invite.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}SENDEREMAIL{'}'}</td>
                                        <td>The email address of the person sending the survey invite.</td>
                                    </tr>
                                    <tr>
                                        <td>#{'{'}SENDERORGANIZATION{'}'}</td>
                                        <td>The name of the organization of the person sending the survey invite.</td>
                                    </tr>
                                </tbody>
                            </table>;

                            const props = {
                                show: text.showModal === true,
                                onHide: (data: string) => {
                                    text.showModal = false;
                                    text.value = data;
                                    this.forceUpdate();
                                },
                                title: 'Content',
                                content: text.value || '',
                                extra: availableKeys
                            };

                            return <tr key={`styletext-${index}`}>
                                <td>
                                    <Select className="form-control" field={['surveyTexts', index, 'languageId']} options={languageOpts} />
                                </td>
                                <td>
                                    <a className="btn btn-default" onClick={(e) => { text.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                    <PonderaEditModal {...props} />
                                </td>
                            </tr>;
                        })
                    }
                </table>
                <div className="form-group">
                    <div className="row">
                        <div className="col-xs-4">
                            <a className="btn btn-default form-control" onClick={(e) => this.add(formApi.values, type)}>Add</a>
                        </div>
                    </div>
                </div>
            </div>);
    }

    private add(formValues: any, type: string) {
        var st = new SurveyText();
        st.surveyTextType = type;
        st.languageId = 1;
        st.keyName = type;
        formValues.surveyTexts.push(st);
        this.forceUpdate();
    }
}


// WEBPACK FOOTER //
// ./src/components/survey/SurveyForm.tsx